import { yearMonthDayPatternDashSeparator as yearFirstDatePattern } from '@oneview/constants/datePattern';
import { createQueryFn } from '@oneview/redux/utils';
import {
    BondQuote,
    BondTimeRange,
    formatDate,
    getBondStartMonth,
    OptionChain,
    Quote,
    QuoteHistory,
    TimeRange,
} from '@orbis-systems/orbis-api-middleware';
import { api } from './baseApi';

export const quoteApi = api.injectEndpoints({
    endpoints: (build) => ({
        getQuote: build.query<Quote | null, { symbol: string; crypto?: boolean }>({
            queryFn: createQueryFn(({ symbol, crypto = false }, { extra: api }) =>
                api.orbisMiddleware.quote.getQuote(symbol, crypto),
            ),
        }),
        getBondQuote: build.query<BondQuote | null, string>({
            queryFn: createQueryFn((id: string, { extra: api }) => api.orbisMiddleware.quote.getBondQuote(id)),
        }),
        getQuoteAccountTypesBreakdown: build.query({
            queryFn: createQueryFn((symbol: string, { extra: api }) =>
                api.orbisMiddleware.stat.getQuoteAccountTypesBreakdownStat(symbol),
            ),
        }),
        getQuoteAccountSizesBreakdown: build.query({
            queryFn: createQueryFn((symbol: string, { extra: api }) =>
                api.orbisMiddleware.stat.getQuoteAccountSizesBreakdownStat(symbol),
            ),
        }),
        searchQuotes: build.query({
            queryFn: createQueryFn(
                async (
                    {
                        criteria,
                        timestamp,
                        limit,
                        disableBonds,
                        disableEquities,
                        disableMutualFunds,
                        onlyMutualFunds,
                    }: {
                        criteria: string;
                        timestamp: number;
                        limit?: number;
                        disableBonds?: boolean;
                        disableEquities?: boolean;
                        disableMutualFunds?: boolean;
                        onlyMutualFunds?: boolean;
                    },
                    { extra: api },
                ) => {
                    const equityQuotes = disableEquities
                        ? []
                        : await api.orbisMiddleware.quote.searchQuotes({
                              criteria,
                              limit,
                              disableMutualFunds: disableMutualFunds && !onlyMutualFunds,
                          });
                    const bondQuotes = disableBonds
                        ? []
                        : await api.orbisMiddleware.quote.searchBondQuotes(criteria, limit);
                    const quotes = onlyMutualFunds ? equityQuotes.filter((quote) => quote.mutualFund) : equityQuotes;
                    const data = [...quotes, ...bondQuotes];
                    return {
                        timestamp: timestamp,
                        results: data,
                    };
                },
            ),
        }),
        getQuotes: build.query({
            queryFn: createQueryFn(
                ({ symbols, crypto = false }: { symbols: string[]; crypto?: boolean }, { extra: api }) => {
                    return api.orbisMiddleware.quote.getQuotes(symbols, crypto);
                },
            ),
        }),
        getBondHistorical: build.query({
            queryFn: createQueryFn(async ({ symbol, range }: { symbol: string; range: TimeRange }, { extra: api }) => {
                const bondStartDate = getBondStartMonth(range);
                return api.orbisMiddleware.stat.getBondHistorical(
                    symbol,
                    range === TimeRange.OneDay ? BondTimeRange.FifteenMinute : BondTimeRange.OneDay,
                    bondStartDate,
                    formatDate(new Date(), { pattern: yearFirstDatePattern }),
                );
            }),
        }),
        getQuoteHistorical: build.query({
            queryFn: createQueryFn(
                async (
                    {
                        symbol,
                        range,
                        crypto,
                        option,
                        bond,
                    }: {
                        symbol: string;
                        range: TimeRange;
                        crypto?: boolean;
                        option?: boolean;
                        bond?: boolean;
                    },
                    { extra: api },
                ) => {
                    const bondStartDate = getBondStartMonth(range);
                    return bond
                        ? api.orbisMiddleware.stat.getBondHistorical(
                              symbol,
                              range === TimeRange.OneDay ? BondTimeRange.FifteenMinute : BondTimeRange.OneDay,
                              bondStartDate,
                              formatDate(new Date(), { pattern: yearFirstDatePattern }),
                          )
                        : option
                          ? api.orbisMiddleware.stat.getQuoteOptionStat(symbol, range)
                          : api.orbisMiddleware.stat.getQuoteStat(symbol, range, crypto);
                },
            ),
        }),
        getIpoDaysOnMarket: build.query({
            queryFn: createQueryFn((symbol: string, { extra: api }) =>
                api.orbisMiddleware.research.getIpoDaysOnMarket(symbol),
            ),
        }),
        getQuoteAnalystsChart: build.query<QuoteHistory[], string>({
            queryFn: createQueryFn(async (symbol: string, { extra: api }) => {
                const data = await api.orbisMiddleware.stat.getQuoteHistorical(symbol, TimeRange.SevenMonth);
                return data;
            }),
        }),
        getQuoteShortability: build.query({
            queryFn: createQueryFn(async (symbol: string, { extra: api }) =>
                api.orbisMiddleware.quote.getQuoteShortability(symbol),
            ),
        }),
        getQuoteProfile: build.query({
            queryFn: createQueryFn((symbol: string, { extra: api }) =>
                api.orbisMiddleware.quote.getQuoteProfile(symbol),
            ),
        }),
        getQuoteIntraday: build.query<QuoteHistory[], string>({
            queryFn: createQueryFn((symbol: string, { extra: api }) =>
                api.orbisMiddleware.stat.getQuoteIntraday(symbol),
            ),
        }),
        getQuoteCryptoHistorical: build.query({
            queryFn: createQueryFn(
                async (
                    {
                        symbol,
                        range,
                    }: {
                        symbol: string;
                        range: TimeRange;
                    },
                    { extra: api },
                ) => {
                    return api.orbisMiddleware.stat.getCryptoHistorical(symbol, range);
                },
            ),
        }),
        getQuoteOption: build.query({
            queryFn: createQueryFn(async (symbol: string, { extra: api }) =>
                api.orbisMiddleware.quote.getOptionQuotes([symbol]),
            ),
        }),
        getQuoteOptionChainDates: build.query({
            queryFn: createQueryFn(async (symbol: string, { extra: api }) =>
                api.orbisMiddleware.quote.getQuoteOptionChainDates(symbol),
            ),
        }),
        getQuoteOptionChainQuotes: build.query<OptionChain, { symbol: string; date: string }>({
            queryFn: createQueryFn(async ({ symbol, date }, { extra: api }) =>
                api.orbisMiddleware.quote.getQuoteOptionChainQuotes(symbol, date),
            ),
        }),
        getTopTenStocks: build.query({
            queryFn: createQueryFn((_: void, { extra: api }) => {
                return api.orbisMiddleware.quote.getMostActiveStocks({
                    paging: {
                        limit: 10,
                        offset: 0,
                    },
                    tipranks: false,
                });
            }),
        }),
        getQuotesFxRate: build.query({
            queryFn: createQueryFn(async ({ symbols }: { symbols: string[] }, { extra: api }) => {
                return api.orbisMiddleware.quote.getQuotesFxRate(symbols);
            }),
        }),
    }),
});

// Export hooks for use in components
export const {
    useGetQuoteQuery,
    useGetBondQuoteQuery,
    useGetQuoteAccountTypesBreakdownQuery,
    useGetQuoteAccountSizesBreakdownQuery,
    useLazySearchQuotesQuery,
    useGetQuotesQuery,
    useGetBondHistoricalQuery,
    useLazyGetBondHistoricalQuery,
    useGetQuoteHistoricalQuery,
    useGetIpoDaysOnMarketQuery,
    useGetQuoteAnalystsChartQuery,
    useGetQuoteShortabilityQuery,
    useGetQuoteProfileQuery,
    useGetQuoteIntradayQuery,
    useGetQuoteCryptoHistoricalQuery,
    useGetQuoteOptionQuery,
    useGetQuoteOptionChainDatesQuery,
    useGetQuoteOptionChainQuotesQuery,
    useGetTopTenStocksQuery,
    useGetQuotesFxRateQuery,
    useLazyGetQuoteQuery,
} = quoteApi;
